import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import { DefinitionTemplate } from './useCreateEvent';
import { useCallback, useEffect, useRef, useState } from 'react';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import {
    EXPERIENCE_TEMPLATE_TYPE,
    PROCESSING_FLOW_TEMPLATE_TYPE,
} from '../constants';

export function useEventExperience(activationId: number) {
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();
    const [isLoading, setIsLoading] = useState(false);
    const [platform, setPlatform] = useState<'clickerbooth' | 'romr' | null>(
        null,
    );
    const [experience, setExperience] = useState<DefinitionTemplate | null>(
        null,
    );
    const isUpdatingExperience = useRef(false);

    const [eventTemplate, setEventTemplate] =
        useState<DefinitionTemplate | null>(null);
    const [processingFlow, setProcessingFlow] =
        useState<DefinitionTemplate | null>(null);
    const [experienceTemplates, setExperienceTemplates] = useState<
        DefinitionTemplate[]
    >([]);

    const [processingFlowTemplates, setProcessingFlowTemplates] = useState<
        DefinitionTemplate[]
    >([]);

    const [processingFlowTemplate, setProcessingFlowTemplate] =
        useState<DefinitionTemplate | null>(null);
    const [templateValues, setTemplateValues] = useState<Record<string, any>>(
        {},
    );

    useEffect(() => {
        const fetchExperienceTemplates = async () => {
            const response = await authenticatedApi.get(
                `/${orgId}/experience-templates`,
            );

            const templates = response.data;
            const experiences = templates.filter(
                (template: any) =>
                    template.templateType === EXPERIENCE_TEMPLATE_TYPE,
            );
            const processingFlows = templates.filter(
                (template: any) =>
                    template.templateType === PROCESSING_FLOW_TEMPLATE_TYPE,
            );

            setExperienceTemplates(experiences);
            setProcessingFlowTemplates(processingFlows);
        };
        fetchExperienceTemplates();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setIsLoading(true);
        authenticatedApi
            .get(`${orgId}/activations/v2/${activationId}/experience`)
            .then((response) => {
                const data = response.data;

                if (data.experience) {
                    setPlatform('clickerbooth');
                    parseExperience(data.experience);
                }
                if (data.processingFlow) {
                    setPlatform('romr');
                    parseProcessingFlow(data.processingFlow);
                }

                setIsLoading(false);
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setIsLoading(false);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    const parseExperience = (experienceString: string) => {
        const parsedExperience = JSON.parse(experienceString);
        const experienceTemplate = parsedExperience.templateValues;
        const values: Record<string, any> = {};

        Object.keys(experienceTemplate).forEach((key) => {
            const templateObject = experienceTemplate[key];
            values[key] = templateObject.value;
        });
        setTemplateValues(values);
        setExperience({
            name: '',
            description: '',
            definitionJsonString: experienceString,
        });
    };

    const parseProcessingFlow = (processingFlowString: string) => {
        const parsedProcessingFlow = JSON.parse(processingFlowString);
        const processingFlowTemplate = parsedProcessingFlow.templateValues;
        const values: Record<string, any> = {};

        Object.keys(processingFlowTemplate).forEach((key) => {
            const templateObject = processingFlowTemplate[key];
            values[key] = templateObject.value;
        });
        setTemplateValues(values);
        setProcessingFlowTemplate({
            name: '',
            description: '',
            definitionJsonString: processingFlowString,
        });
    };

    const recordValues = useCallback(
        (
            values: Record<
                string,
                string | Date | boolean | DefinitionTemplate | null | number
            >,
        ) => {
            for (const [key, value] of Object.entries(values)) {
                if (key === 'eventTemplate') {
                    console.log('eventTemplate', value);
                    setEventTemplate(value as unknown as DefinitionTemplate);
                } else if (key === 'experience') {
                    setExperience(value as DefinitionTemplate);
                } else if (key === 'processingFlow') {
                    setProcessingFlow(value as DefinitionTemplate);
                } else if (key === 'processingFlowTemplate') {
                    setProcessingFlowTemplate(value as DefinitionTemplate);
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const saveExperience = async () => {
        if (isUpdatingExperience.current) {
            return;
        }
        isUpdatingExperience.current = true;
        const experienceDefinition = eventTemplate;
        const processingFlowDefinition = processingFlow;

        const requestBody = {
            experience: {
                experience: experienceDefinition,
                processingFlow: processingFlowDefinition,
            },
        };

        try {
            await authenticatedApi.put(
                `/${orgId}/activations/v2/${activationId}/experience`,
                requestBody,
            );
        } catch (error) {
            console.error(error);
        } finally {
            isUpdatingExperience.current = false;
        }
    };

    return {
        isLoading,
        platform,
        experience,
        processingFlowTemplate,
        templateValues,
        setExperience,
        setProcessingFlowTemplate,
        setTemplateValues,
        experienceTemplates,
        processingFlowTemplates,
        recordValues,
        saveExperience,
    };
}
